import React from "react";

import AudienceSynthesisReport from "./AudienceSynthesisReport";

/*
 * Audience Data Synthesis panel — presentational. All content comes from the
 * view model built by buildAudienceSynthesisViewModel over the canonical
 * audience-intelligence resolution; this component renders states, it never
 * fetches and never invents fields the canonical record does not carry.
 *
 * The synthesis body is rendered from viewModel.report — one canonical body,
 * already structured and already de-duplicated by the presentation layer. The
 * panel deliberately does NOT also render viewModel.summary and
 * viewModel.narrative as separate paragraphs: those payload properties carry
 * the same analysis, and printing each in turn is what put the whole synthesis
 * on screen twice.
 */

export default function AudienceSynthesisPanel({ viewModel, profile, role = "superadmin" }) {
  if (!viewModel) return null;
  const heroLabel = profile?.heroLabel || "Platform";

  let body = null;
  if (viewModel.state === "idle") {
    body = (
      <p className="ppx-state-body">
        Choose an advertiser to resolve the governed audience synthesis for {heroLabel}.
      </p>
    );
  } else if (viewModel.state === "loading") {
    body = (
      <p className="ppx-state-body" role="status">
        Resolving the latest governed audience synthesis…
      </p>
    );
  } else if (viewModel.state === "error") {
    body = (
      <p className="ppx-state-body" role="alert">
        {viewModel.message}
      </p>
    );
  } else if (viewModel.state === "unavailable") {
    body = <p className="ppx-state-body">{viewModel.message}</p>;
  } else if (viewModel.state === "planner") {
    body = (
      <>
        {viewModel.summary && <p className="ppx-audience-prose">{viewModel.summary}</p>}
        {viewModel.bullets.length > 0 && (
          <ul className="ppx-audience-bullets">
            {viewModel.bullets.map((bullet, index) => (
              <li key={index}>{bullet}</li>
            ))}
          </ul>
        )}
        <p className="ppx-audience-note" role="note">
          This is approved planning targeting shown as context — it is not a completed audience
          analysis, and no synthesis is fabricated in its place.
        </p>
      </>
    );
  } else {
    body = (
      <>
        {viewModel.sharedLabel && (
          <p className="ppx-audience-shared-label" role="note">
            {viewModel.sharedLabel}
          </p>
        )}
        {viewModel.agedLabel && (
          <p className="ppx-audience-aged-label" role="note">
            ⚠ {viewModel.agedLabel}
          </p>
        )}
        <AudienceSynthesisReport report={viewModel.report} />
        <dl className="ppx-audience-facts">
          {viewModel.confidenceLabel && (
            <div>
              <dt>Confidence</dt>
              <dd>{viewModel.confidenceLabel}</dd>
            </div>
          )}
          {viewModel.dateRangeLabel && (
            <div>
              <dt>Coverage</dt>
              <dd>{viewModel.dateRangeLabel}</dd>
            </div>
          )}
          {viewModel.updatedAtLabel && (
            <div>
              <dt>Last updated</dt>
              <dd>{viewModel.updatedAtLabel}</dd>
            </div>
          )}
          {role === "superadmin" && viewModel.provenance && (
            <>
              <div>
                <dt>Source</dt>
                <dd>
                  {viewModel.provenance.sourceName ||
                    viewModel.provenance.sourceType ||
                    viewModel.provenance.source}
                </dd>
              </div>
              <div>
                <dt>Resolution</dt>
                <dd>
                  {viewModel.provenance.resolvedPlatform || "—"}
                  {viewModel.provenance.isFallback ? " · governed fallback" : " · direct match"}
                </dd>
              </div>
            </>
          )}
        </dl>
        {viewModel.note && <p className="ppx-audience-note">{viewModel.note}</p>}
        {viewModel.disclaimer && (
          <p className="ppx-audience-disclaimer" role="note">
            {viewModel.disclaimer}
          </p>
        )}
      </>
    );
  }

  const statusBadge =
    viewModel.state === "synthesis" || viewModel.state === "planner"
      ? viewModel.statusLabel
      : null;

  return (
    <section className="ppx-panel ppx-panel--audience" aria-label="Audience Data Synthesis">
      <div className="ppx-panel-header">
        <div>
          <h2 className="ppx-panel-title">Audience Data Synthesis</h2>
          <p className="ppx-panel-subtitle">
            Governed Audience Intelligence for {heroLabel} — canonical records only, nothing
            inferred
          </p>
        </div>
        {statusBadge && <span className="ppx-panel-badge">{statusBadge}</span>}
      </div>
      {body}
      <p className="ppx-audience-footer">
        Read-only. This page consumes the governed audience synthesis — it never generates,
        modifies, or re-targets anything.
      </p>
    </section>
  );
}
